// business-dashboard/src/components/blockchain/BlockchainComponents.tsx
//
// Reusable UI components for blockchain tier management.
// Drop these anywhere you need conditional blockchain UI.

import React, { useState } from 'react';
import { Link2, Shield, AlertCircle, CheckCircle, Lock, Zap, Crown } from 'lucide-react';
import { useBlockchainAccess, getBlockchainReasonMessage, BlockchainTier } from '../../hooks/useBlockchainAccess';
import BusinessApiService from '../../services/BusinessApiService';
import { getCurrentBusinessId } from '../../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ════════════════════════════════════════════════════════
// 1. <BlockchainGate> — wrap any UI that requires blockchain
// ════════════════════════════════════════════════════════
//
// Usage:
//   <BlockchainGate fallback={<UpsellCard />}>
//     <BatchTraceUrlField />
//   </BlockchainGate>
//
// If the customer can use blockchain → renders children
// If not → renders fallback (or nothing)

export const BlockchainGate: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAllowed?: boolean; // true = strict (all checks pass), false = just subscribed
}> = ({ children, fallback = null, requireAllowed = true }) => {
  const blockchain = useBlockchainAccess();

  if (blockchain.loading) return null;

  const passes = requireAllowed
    ? blockchain.allowed
    : blockchain.subscribed;

  if (passes) return <>{children}</>;
  return <>{fallback}</>;
};

// ════════════════════════════════════════════════════════
// 2. <BlockchainTierBadge> — small pill showing the tier
// ════════════════════════════════════════════════════════

export const BlockchainTierBadge: React.FC<{ tier?: BlockchainTier }> = ({ tier }) => {
  const blockchain = useBlockchainAccess();
  const actualTier = tier || blockchain.tier;

  if (actualTier === 'none') return null;

  const styles: Record<BlockchainTier, string> = {
    none: '',
    basic: 'bg-blue-50 text-blue-700 border-blue-200',
    premium: 'bg-purple-50 text-purple-700 border-purple-200',
    enterprise: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  const icons: Record<BlockchainTier, React.ReactNode> = {
    none: null,
    basic: <Shield className="w-3 h-3" />,
    premium: <Zap className="w-3 h-3" />,
    enterprise: <Crown className="w-3 h-3" />,
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[actualTier]}`}
    >
      {icons[actualTier]}
      {actualTier.charAt(0).toUpperCase() + actualTier.slice(1)} Blockchain
    </span>
  );
};

// ════════════════════════════════════════════════════════
// 3. <BlockchainUpsellCard> — shown to non-subscribed customers
// ════════════════════════════════════════════════════════

export const BlockchainUpsellCard: React.FC<{
  title?: string;
  description?: string;
  ctaText?: string;
  onUpgrade?: () => void;
}> = ({
  title = 'Add tamper-proof verification',
  description = 'Anchor your batches on a public blockchain for unbreakable proof of authenticity. Customers can independently verify your products.',
  ctaText = 'Upgrade to Premium',
  onUpgrade,
}) => (
  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-dashed border-purple-300 rounded-2xl p-6">
    <div className="flex items-start gap-4">
      <div className="bg-purple-600 text-white rounded-xl p-3 flex-shrink-0">
        <Lock className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-purple-900 mb-1">{title}</h3>
        <p className="text-sm text-purple-700 mb-4 leading-relaxed">{description}</p>
        <ul className="text-xs text-purple-700 space-y-1.5 mb-4">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 text-purple-600" />
            Permanent on-chain proof per batch
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 text-purple-600" />
            Independent customer verification
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 text-purple-600" />
            Anti-counterfeit guarantee
          </li>
        </ul>
        {onUpgrade && (
          <button
            onClick={onUpgrade}
            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            {ctaText} →
          </button>
        )}
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════
// 4. <BlockchainStatusBanner> — shows reason when not allowed
// ════════════════════════════════════════════════════════
// Useful at top of pages where blockchain features matter.

export const BlockchainStatusBanner: React.FC = () => {
  const blockchain = useBlockchainAccess();

  if (blockchain.loading) return null;
  if (blockchain.allowed) return null; // Don't show anything if working

  const message = getBlockchainReasonMessage(blockchain.reason);
  if (!message) return null;

  // Don't show if they're not even subscribed (the upsell card covers that)
  if (blockchain.reason === 'tier_not_subscribed') return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3 mb-4">
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <span className="text-sm text-amber-800">{message}</span>
    </div>
  );
};

// ════════════════════════════════════════════════════════
// 5. <BlockchainSettingsPanel> — full settings card for the business
// ════════════════════════════════════════════════════════

export const BlockchainSettingsPanel: React.FC = () => {
  const blockchain = useBlockchainAccess();
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    if (!blockchain.subscribed) return;
    setToggling(true);
    setError(null);
    try {
      const businessId = getCurrentBusinessId();
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/business/${businessId}/blockchain/toggle`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !blockchain.enabled }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Toggle failed');
      } else {
        await blockchain.refresh();
      }
    } catch (e: any) {
      setError(e.message);
    }
    setToggling(false);
  };

  if (blockchain.loading) {
    return (
      <div className="bg-white border rounded-xl p-6">
        <div className="animate-pulse h-20" />
      </div>
    );
  }

  // Not subscribed → show upsell
  if (!blockchain.subscribed) {
    return <BlockchainUpsellCard />;
  }

  // Subscribed → show settings
  const usagePct =
    blockchain.monthlyAnchorLimit > 0
      ? (blockchain.monthlyAnchorsUsed / blockchain.monthlyAnchorLimit) * 100
      : 0;

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100 p-5 flex items-start gap-4">
        <div className="bg-purple-600 text-white rounded-xl p-2.5">
          <Link2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-base font-bold text-purple-900">Blockchain Verification</h3>
            <BlockchainTierBadge tier={blockchain.tier} />
          </div>
          <p className="text-xs text-purple-700">
            Network: <span className="font-mono font-semibold">{blockchain.network}</span>
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-semibold text-gray-900">Anchor batches on chain</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {blockchain.enabled
                ? 'New batches will be anchored on activation'
                : 'Blockchain anchoring is paused'}
            </div>
          </div>
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              blockchain.enabled ? 'bg-purple-600' : 'bg-gray-300'
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                blockchain.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Usage meter */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-baseline mb-2">
            <span className="text-sm font-semibold text-gray-900">Monthly anchors</span>
            <span className="text-xs text-gray-500">
              {blockchain.monthlyAnchorsUsed} / {blockchain.monthlyAnchorLimit}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                usagePct >= 90
                  ? 'bg-red-500'
                  : usagePct >= 70
                  ? 'bg-amber-500'
                  : 'bg-purple-600'
              }`}
              style={{ width: `${Math.min(usagePct, 100)}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1.5">
            {blockchain.remainingAnchors} anchors remaining this month
          </div>
        </div>

        {/* Status */}
        {!blockchain.allowed && blockchain.reason && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 inline mr-1" />
            {getBlockchainReasonMessage(blockchain.reason)}
          </div>
        )}
      </div>
    </div>
  );
};
